import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { dayToDayAccounts } from '../utils/accounts';
import ImportMappings from './ImportMappings';
import '../styles/forms.css';
import '../styles/ImportTransactions.css';

const ManageImportMappings = () => {
  const [accounts, setAccounts] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [mappings, setMappings] = useState({});
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, subjectsRes, mappingsRes, locationRes] = await Promise.all([
          api.get('/accounts'),
          api.get('/subjects'),
          api.get('/usersettings/import-mappings').catch(() => ({ data: {} })),
          api.get('/usersettings/merchant-lookup-location').catch(() => ({ data: {} })),
        ]);

        setAccounts(dayToDayAccounts((accountsRes.data || []).filter((a) => a.is_open)).sort((a, b) => a.name.localeCompare(b.name)));
        setSubjects((subjectsRes.data || []).sort((a, b) => a.name.localeCompare(b.name)));

        const normalized = {};
        let idx = 0;
        for (const [original, mappingData] of Object.entries(mappingsRes.data?.mappings || mappingsRes.data || {})) {
          if (typeof mappingData === 'object' && mappingData !== null) {
            normalized[idx] = {
              original,
              type: mappingData.type || 'subject',
              subject_id: mappingData.type === 'subject' ? mappingData.id : null,
              account_id: mappingData.type === 'account' ? mappingData.id : null,
            };
          } else {
            normalized[idx] = {
              original,
              type: 'subject',
              subject_id: Number(mappingData),
              account_id: null,
            };
          }
          idx++;
        }
        setMappings(normalized);
        setLocation(
          typeof locationRes.data?.value === 'string' ? locationRes.data.value : ''
        );
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load mappings.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const submitMappings = {};
      Object.values(mappings).forEach((entry) => {
        if (entry && entry.original) {
          submitMappings[entry.original] = {
            type: entry.type,
            id: entry.type === 'subject' ? entry.subject_id : entry.account_id,
          };
        }
      });
      await api.put('/usersettings/import-mappings', submitMappings);
      await api.put('/usersettings/merchant-lookup-location', { value: location });
      setSaved(true);
    } catch (err) {
      setError('Failed to save mappings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2 className="page-title">Manage Import Mappings</h2>

      <p className="mappings-intro">
        Define how original descriptions from bank statements are mapped to subjects
        or accounts when importing transactions.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <ImportMappings
          mappings={mappings}
          onChange={setMappings}
          accounts={accounts}
          subjects={subjects}
          onSubjectCreated={(subject) =>
            setSubjects((prev) => (prev.some((s) => s.id === subject.id) ? prev : [...prev, subject]))
          }
        />
      )}

      <div className="form-group">
        <label htmlFor="merchant-lookup-location">Merchant lookup location</label>
        <input
          id="merchant-lookup-location"
          type="text"
          value={location}
          placeholder="e.g. Malta, Valletta, Rome"
          onChange={(e) => setLocation(e.target.value)}
        />
        <p className="mappings-intro">
          Optional. Added to web searches that identify what kind of place an
          unknown merchant is during imports.
        </p>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="form-buttons">
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {saved && <p className="mappings-saved">Saved.</p>}
    </div>
  );
};

export default ManageImportMappings;